import React, { useState } from 'react';
import { Dialog, DialogContent, Box, IconButton, Typography, TextField, Button, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { requestPasswordResetEmail } from '@/services/auth.service';
import { toast } from 'react-toastify';
import { ServiceResponseType } from '@/helpers/serviceParsing';
import { useLoadingDialogStore } from '@/store/loadingDialogStore';
import axios from 'axios';
import config from '@/config/config';

const { user_api } = config.api;

interface PasswordResetDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function PasswordResetDialog({ open, onClose }: PasswordResetDialogProps) {
    const { setIsLoadingOpen } = useLoadingDialogStore((state) => state.action);
    const [isEmailSent, setIsEmailSent] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const handleClickClose = () => {
        onClose();
        reset({
            email: '',
        });
        setIsEmailSent(false);
    };

    const handleSendEmail = () => {
        handleSubmit(async (data) => {
            try {
                setIsLoadingOpen(true);
                const { email } = data;
                const response = await axios.post(`${user_api}/password/email/renew/request`, {
                    email,
                    is_shop3: true,
                });
                if (response.status === 200 || response.status === 201) {
                    setIsEmailSent(true);
                } else {
                    toast.error(response?.data?.place_holder || '이메일 발송에 실패했습니다. 다시 시도해주세요.');
                }
            } catch (error) {
                const e = error as ServiceResponseType;
                console.log(e);
                toast.error(e?.place_holder || '이메일 발송에 실패했습니다. 다시 시도해주세요.');
            } finally {
                setIsLoadingOpen(false);
            }
        })();
    };

    const emailRegister = register('email', {
        required: '이메일을 입력해주세요.',
        pattern: {
            value: /^([0-9a-zA-Z_\.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/,
            message: '이메일 형식이 아닙니다.',
        },
    });

    return (
        <Dialog
            open={open}
            onClose={handleClickClose}
            maxWidth="xs"
            fullWidth
            sx={{
                zIndex: 1500,
                '& .MuiDialog-paper': {
                    borderRadius: '12px !important',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                },
            }}
        >
            <DialogContent sx={{ p: 0, position: 'relative' }}>
                {/* 닫기 버튼 */}
                <IconButton
                    onClick={handleClickClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        zIndex: 1,
                        color: '#000',
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ p: 4, pt: 6 }}>
                    {!isEmailSent ? (
                        <>
                            {/* 제목 */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '28px',
                                        color: '#222',
                                        mb: 1,
                                    }}
                                >
                                    비밀번호 찾기
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        color: '#666',
                                        lineHeight: '1.5',
                                    }}
                                >
                                    가입하신 이메일 주소를 입력하시면
                                    <br />
                                    비밀번호 재설정 링크를 보내드립니다.
                                </Typography>
                            </Box>

                            {/* 이메일 입력 필드 */}
                            <Box sx={{ mb: 4 }}>
                                <TextField
                                    {...emailRegister}
                                    placeholder="이메일 주소를 입력하세요"
                                    fullWidth
                                    variant="outlined"
                                    size="medium"
                                    error={!!errors.email}
                                    helperText={String(errors.email?.message || '')}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#e0e8f7',
                                            border: '1px solid #000',
                                            borderRadius: '4px',
                                            '&:hover': {
                                                borderColor: '#000',
                                            },
                                            '&.Mui-focused': {
                                                borderColor: '#000',
                                                borderWidth: '1px',
                                            },
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: '#000',
                                            fontSize: '16px',
                                            padding: '12px 16px',
                                            '&:-webkit-autofill': {
                                                WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                                WebkitTextFillColor: '#000',
                                            },
                                            '&:-webkit-autofill:hover': {
                                                WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                            },
                                            '&:-webkit-autofill:focus': {
                                                WebkitBoxShadow: '0 0 0 1000px #e0e8f7 inset',
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            {/* 버튼 영역 */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleClickClose}
                                    sx={{
                                        borderColor: '#ddd',
                                        color: '#666',
                                        py: 1.5,
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        '&:hover': {
                                            borderColor: '#999',
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    취소
                                </Button>
                                <Button
                                    onClick={handleSendEmail}
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        backgroundColor: '#FF4B6B',
                                        color: '#fff',
                                        py: 1.5,
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: '#E03E5A',
                                        },
                                    }}
                                >
                                    이메일 전송
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            {/* 성공 메시지 */}
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '18px',
                                        color: '#222',
                                        mb: 2,
                                    }}
                                >
                                    이메일이 발송되었습니다
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        color: '#666',
                                        lineHeight: '1.5',
                                        mb: 3,
                                    }}
                                >
                                    입력하신 이메일 주소로
                                    <br />
                                    비밀번호 재설정 링크를 보내드렸습니다.
                                    <br />
                                    이메일을 확인해주세요.
                                </Typography>
                            </Box>

                            {/* 확인 버튼 */}
                            <Button
                                onClick={handleClickClose}
                                fullWidth
                                variant="contained"
                                sx={{
                                    mb: 3,
                                    backgroundColor: '#FF4B6B',
                                    color: '#fff',
                                    py: 1.5,
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#E03E5A',
                                    },
                                }}
                            >
                                확인
                            </Button>
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
